/**
 * WALL OWNERSHIP ENFORCEMENT SYSTEM
 * 
 * This module enforces strict wall ownership during resize operations to prevent
 * coordinate collision issues. It ensures that when resizing a room, only walls
 * that belong to that specific room are affected, even if other walls exist at
 * identical coordinates.
 */

import { Wall, Point, Room } from '../types';

export interface WallWithTempId extends Wall {
  tempId?: string;
  originatingRoomId?: string;
  assignedRoomIds?: string[];
  parentRoomIds?: string[];
  uniqueWallId?: string; // New: Unique identifier that combines room + coordinates
}

export interface RoomSpecificWallFiltering {
  originalWalls: WallWithTempId[];
  roomSpecificWalls: WallWithTempId[];
  excludedWalls: WallWithTempId[];
  coordinateCollisions: number;
}

/**
 * Generates a unique wall identifier that combines room ownership with coordinates
 * This prevents coordinate collision confusion during resize operations
 */
export function generateUniqueWallId(wall: WallWithTempId, roomId: string): string {
  const coordKey = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}-${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
  return `${roomId}:${coordKey}:${wall.tempId || 'no-temp-id'}`;
}

/**
 * Enhanced wall filtering that ensures only walls belonging to the specific room are included
 * This is the core function that prevents coordinate collision issues during resize
 */
export function filterWallsByRoomOwnership(
  allWalls: WallWithTempId[],
  targetRoomId: string,
  excludeSharedWalls: boolean = false
): RoomSpecificWallFiltering {
  const roomSpecificWalls: WallWithTempId[] = [];
  const excludedWalls: WallWithTempId[] = [];
  let coordinateCollisions = 0;

  console.log('🔒 FILTERING WALLS BY ROOM OWNERSHIP:', {
    targetRoomId,
    totalWalls: allWalls.length,
    excludeSharedWalls
  });

  // Track coordinate collisions
  const coordinateMap = new Map<string, WallWithTempId[]>();

  // Group walls by coordinates to detect collisions
  allWalls.forEach(wall => {
    const coordKey = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}-${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
    if (!coordinateMap.has(coordKey)) {
      coordinateMap.set(coordKey, []);
    }
    coordinateMap.get(coordKey)!.push(wall);
  });

  // Process each wall
  allWalls.forEach(wall => {
    const belongsToRoom = checkWallBelongsToRoom(wall, targetRoomId);
    const coordKey = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}-${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
    const wallsAtSameCoordinates = coordinateMap.get(coordKey) || [];
    
    // Check for coordinate collision
    if (wallsAtSameCoordinates.length > 1) {
      coordinateCollisions++;
      
      // For coordinate collisions, be extra strict about room ownership
      const hasExactOwnership = wall.originatingRoomId === targetRoomId || 
                               wall.assignedRoomIds?.includes(targetRoomId);
      
      if (hasExactOwnership) {
        // Generate unique wall ID to prevent confusion
        const enhancedWall: WallWithTempId = {
          ...wall,
          uniqueWallId: generateUniqueWallId(wall, targetRoomId)
        };
        roomSpecificWalls.push(enhancedWall);
        
        console.log('✅ COORDINATE COLLISION RESOLVED:', {
          wallId: wall.tempId,
          roomId: targetRoomId,
          coordinates: coordKey,
          conflictingWalls: wallsAtSameCoordinates.length,
          uniqueWallId: enhancedWall.uniqueWallId
        });
      } else {
        excludedWalls.push(wall);
        
        console.log('🚫 COORDINATE COLLISION EXCLUDED:', {
          wallId: wall.tempId,
          roomId: targetRoomId,
          wallOwnership: {
            originatingRoomId: wall.originatingRoomId,
            assignedRoomIds: wall.assignedRoomIds
          },
          coordinates: coordKey,
          reason: 'Wall belongs to different room'
        });
      }
    } else {
      // No collision, use standard ownership check
      if (belongsToRoom) {
        const enhancedWall: WallWithTempId = {
          ...wall,
          uniqueWallId: generateUniqueWallId(wall, targetRoomId)
        };
        roomSpecificWalls.push(enhancedWall);
      } else {
        excludedWalls.push(wall);
      }
    }
  });

  console.log('🔒 WALL OWNERSHIP FILTERING COMPLETE:', {
    targetRoomId,
    originalWallCount: allWalls.length,
    roomSpecificWallCount: roomSpecificWalls.length,
    excludedWallCount: excludedWalls.length,
    coordinateCollisions
  });

  return {
    originalWalls: allWalls,
    roomSpecificWalls,
    excludedWalls,
    coordinateCollisions
  };
}

/**
 * Checks if a wall belongs to a specific room using comprehensive ownership rules
 */
export function checkWallBelongsToRoom(wall: WallWithTempId, roomId: string): boolean {
  // Primary ownership check
  if (wall.originatingRoomId === roomId) {
    return true;
  }

  // Assigned room check
  if (wall.assignedRoomIds?.includes(roomId)) {
    return true;
  }

  // Legacy parent room check
  if (wall.parentRoomIds?.includes(roomId)) {
    return true;
  }

  // Room ID field check (for older wall formats)
  if ((wall as any).roomId === roomId) {
    return true;
  }

  return false;
}

/**
 * Enhanced room wall filtering for resize operations
 * This ensures only walls that truly belong to the room are included in resize calculations
 */
export function getResizeEligibleWalls(
  room: Room,
  allWalls: WallWithTempId[]
): WallWithTempId[] {
  const roomId = room.stableId || room.id;
  
  // Filter walls by strict room ownership
  const filtering = filterWallsByRoomOwnership(allWalls, roomId, false);
  
  // Additional filtering: only include walls that are actually part of the room's wall array
  // Match by coordinates since room.walls don't have tempIds
  const tolerance = 0.5;
  
  // Map each room wall to the BEST matching wall from roomSpecificWalls
  // This ensures we get exactly one wall per room wall (no duplicates)
  const eligibleWalls: WallWithTempId[] = [];
  const usedWallIds = new Set<string>();
  
  for (const roomWall of room.walls) {
    // Find all candidate walls that match this room wall's coordinates
    const candidates = filtering.roomSpecificWalls.filter(wall => {
      // Skip if already used
      if (wall.tempId && usedWallIds.has(wall.tempId)) return false;
      
      const forwardMatch = 
        Math.abs(roomWall.start.x - wall.start.x) < tolerance &&
        Math.abs(roomWall.start.y - wall.start.y) < tolerance &&
        Math.abs(roomWall.end.x - wall.end.x) < tolerance &&
        Math.abs(roomWall.end.y - wall.end.y) < tolerance;
      
      const reverseMatch = 
        Math.abs(roomWall.start.x - wall.end.x) < tolerance &&
        Math.abs(roomWall.start.y - wall.end.y) < tolerance &&
        Math.abs(roomWall.end.x - wall.start.x) < tolerance &&
        Math.abs(roomWall.end.y - wall.start.y) < tolerance;
      
      return forwardMatch || reverseMatch;
    });
    
    if (candidates.length > 0) {
      // Prefer walls with proper ownership, then walls with tempId, then first match
      const bestCandidate = candidates.find(c => c.originatingRoomId === roomId) 
        || candidates.find(c => c.tempId) 
        || candidates[0];
      
      if (candidates.length > 1) {
        console.log('🔍 DUPLICATE_WALLS_DETECTED:', {
          roomWall: `(${roomWall.start.x.toFixed(1)},${roomWall.start.y.toFixed(1)})->(${roomWall.end.x.toFixed(1)},${roomWall.end.y.toFixed(1)})`,
          candidatesCount: candidates.length,
          candidateIds: candidates.map(c => c.tempId || 'NO_ID'),
          selectedId: bestCandidate.tempId || 'NO_ID'
        });
      }
      
      eligibleWalls.push(bestCandidate);
      if (bestCandidate.tempId) {
        usedWallIds.add(bestCandidate.tempId);
      }
    }
  }

  console.log('🔒 RESIZE ELIGIBLE WALLS FILTERED:', {
    roomId,
    roomName: room.name,
    totalWalls: allWalls.length,
    roomWalls: room.walls.length,
    filteredWalls: filtering.roomSpecificWalls.length,
    eligibleWalls: eligibleWalls.length,
    coordinateCollisions: filtering.coordinateCollisions
  });
  
  // 🔍 DEBUG: Log detailed wall breakdown
  console.log('🔍 ELIGIBLE WALLS BREAKDOWN:', {
    roomWallCoordinates: room.walls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`),
    eligibleWallCoordinates: eligibleWalls.map(w => `${w.tempId || 'NO_ID'}: (${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`),
    allWallsCoordinates: allWalls.map(w => `${w.tempId || 'NO_ID'}: (${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
  });

  return eligibleWalls;
}

/**
 * Validates that resize operations will only affect the correct room's walls
 */
export function validateResizeWallOwnership(
  wallToResize: WallWithTempId,
  roomId: string,
  allWalls: WallWithTempId[]
): {
  isValid: boolean;
  issues: string[];
  conflictingWalls: WallWithTempId[];
  recommendation: string;
} {
  const issues: string[] = [];
  const conflictingWalls: WallWithTempId[] = [];

  // Check if the wall belongs to the room
  if (!checkWallBelongsToRoom(wallToResize, roomId)) {
    issues.push(`Wall ${wallToResize.tempId} does not belong to room ${roomId}`);
  }

  // Check for coordinate collisions
  const coordKey = `${wallToResize.start.x.toFixed(2)},${wallToResize.start.y.toFixed(2)}-${wallToResize.end.x.toFixed(2)},${wallToResize.end.y.toFixed(2)}`;
  
  const wallsAtSameCoordinates = allWalls.filter(wall => {
    const wallCoordKey = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}-${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
    return wallCoordKey === coordKey && wall.tempId !== wallToResize.tempId;
  });

  if (wallsAtSameCoordinates.length > 0) {
    const foreignWalls = wallsAtSameCoordinates.filter(wall => 
      !checkWallBelongsToRoom(wall, roomId)
    );

    if (foreignWalls.length > 0) {
      conflictingWalls.push(...foreignWalls);
      issues.push(`${foreignWalls.length} walls from other rooms exist at the same coordinates`);
    }
  }

  let recommendation = 'Resize operation is safe to proceed';
  if (issues.length > 0) {
    recommendation = 'Use room-specific wall filtering to prevent affecting walls from other rooms';
  }

  return {
    isValid: issues.length === 0,
    issues,
    conflictingWalls,
    recommendation
  };
}

/**
 * Creates a safe wall array for resize operations that excludes conflicting walls
 */
export function createSafeWallArrayForResize(
  targetRoom: Room,
  allWalls: WallWithTempId[]
): {
  safeWalls: WallWithTempId[];
  excludedWalls: WallWithTempId[];
  warnings: string[];
} {
  const warnings: string[] = [];
  const roomId = targetRoom.stableId || targetRoom.id;
  
  // Get only walls that belong to this room
  const eligibleWalls = getResizeEligibleWalls(targetRoom, allWalls);
  
  // Exclude walls that would cause coordinate conflicts
  const safeWalls: WallWithTempId[] = [];
  const excludedWalls: WallWithTempId[] = [];

  eligibleWalls.forEach(wall => {
    const validation = validateResizeWallOwnership(wall, roomId, allWalls);
    
    if (validation.isValid) {
      safeWalls.push(wall);
    } else {
      excludedWalls.push(wall);
      warnings.push(`Wall ${wall.tempId}: ${validation.issues.join(', ')}`);
    }
  });

  console.log('🛡️ SAFE WALL ARRAY CREATED:', {
    roomId,
    roomName: targetRoom.name,
    originalWallCount: allWalls.length,
    eligibleWalls: eligibleWalls.length,
    safeWalls: safeWalls.length,
    excludedWalls: excludedWalls.length,
    warnings: warnings.length
  });

  return {
    safeWalls,
    excludedWalls,
    warnings
  };
}

/**
 * Post-resize validation to ensure only the correct walls were affected
 */
export function validatePostResize(
  originalWalls: WallWithTempId[],
  newWalls: WallWithTempId[],
  roomId: string
): {
  isValid: boolean;
  unexpectedChanges: WallWithTempId[];
  summary: string;
} {
  const unexpectedChanges: WallWithTempId[] = [];

  // Check each original wall to see if it was unexpectedly modified
  originalWalls.forEach(originalWall => {
    const belongsToRoom = checkWallBelongsToRoom(originalWall, roomId);
    
    if (!belongsToRoom) {
      // This wall shouldn't have been modified
      const newWall = newWalls.find(w => w.tempId === originalWall.tempId);
      
      if (newWall && (
        Math.abs(newWall.start.x - originalWall.start.x) > 0.01 ||
        Math.abs(newWall.start.y - originalWall.start.y) > 0.01 ||
        Math.abs(newWall.end.x - originalWall.end.x) > 0.01 ||
        Math.abs(newWall.end.y - originalWall.end.y) > 0.01
      )) {
        unexpectedChanges.push(originalWall);
      }
    }
  });

  const isValid = unexpectedChanges.length === 0;
  const summary = isValid 
    ? `Resize operation correctly affected only walls belonging to room ${roomId}`
    : `Resize operation unexpectedly affected ${unexpectedChanges.length} walls from other rooms`;

  console.log('🔍 POST-RESIZE VALIDATION:', {
    roomId,
    isValid,
    unexpectedChanges: unexpectedChanges.length,
    summary
  });

  return {
    isValid,
    unexpectedChanges,
    summary
  };
}